import type { ErrorEvent } from '@sentry/react';

const DESCOPE_CONSOLE_PREFIX = '[Descope]';
const DESCOPE_RECAPTCHA_REMOVE_MESSAGE = "reading 'remove'";
const DESCOPE_RECAPTCHA_STACK_MARKERS = [
  '@descope/web-components-ui',
  'descope-recaptcha',
] as const;

interface DescopeSentryHint {
  originalException?: unknown;
  syntheticException?: unknown;
}

const getConsoleArguments = (event: ErrorEvent): unknown[] => {
  const args = event.extra?.arguments;
  return Array.isArray(args) ? args : [];
};

const getConsoleMessage = (event: ErrorEvent): string =>
  getConsoleArguments(event)
    .filter((arg): arg is string => typeof arg === 'string')
    .join(' ')
    .trim();

const getErrorMessage = (value: unknown): string => {
  if (typeof value === 'string') {
    return value;
  }
  if (value instanceof Error) {
    return value.message;
  }
  return '';
};

const getErrorStack = (value: unknown): string => {
  if (value instanceof Error && typeof value.stack === 'string') {
    return value.stack;
  }
  return '';
};

const joinPresentStrings = (values: unknown[]): string =>
  values
    .filter((value): value is string => typeof value === 'string')
    .filter(value => value !== '')
    .join('\n');

const getEventMessages = (
  event: ErrorEvent,
  hint?: DescopeSentryHint,
): string =>
  joinPresentStrings([
    event.message,
    ...(event.exception?.values ?? []).map(exception => exception.value),
    getErrorMessage(hint?.originalException),
    getErrorMessage(hint?.syntheticException),
  ]);

const getEventStack = (event: ErrorEvent, hint?: DescopeSentryHint): string => {
  const frameParts = (event.exception?.values ?? []).flatMap(exception =>
    (exception.stacktrace?.frames ?? []).flatMap(frame => [
      frame.abs_path,
      frame.filename,
      frame.module,
    ]),
  );

  return joinPresentStrings([
    ...frameParts,
    getErrorStack(hint?.originalException),
    getErrorStack(hint?.syntheticException),
  ]);
};

export const isDescopeConsoleEvent = (event: ErrorEvent): boolean =>
  event.logger === 'console' &&
  getConsoleArguments(event)[0] === DESCOPE_CONSOLE_PREFIX;

export const isDescopeRecaptchaRemoveEvent = (
  event: ErrorEvent,
  hint?: DescopeSentryHint,
): boolean => {
  const message = getEventMessages(event, hint);
  if (!message.includes(DESCOPE_RECAPTCHA_REMOVE_MESSAGE)) {
    return false;
  }

  const stack = getEventStack(event, hint);
  return DESCOPE_RECAPTCHA_STACK_MARKERS.some(marker => stack.includes(marker));
};

export const enrichDescopeConsoleEvent = (event: ErrorEvent): ErrorEvent => {
  const message = getConsoleMessage(event) || 'Descope flow error';

  event.fingerprint = ['descope-console', message];

  const values = event.exception?.values;
  if (values?.length) {
    values.forEach(value => {
      if (!value.value) {
        value.value = message;
      }
    });
  } else {
    event.message = message;
  }

  return event;
};
